import {
    activeSiteCountry,
    activeSiteCurrency,
    shoeConventionForSite,
} from "@config/site/site-config";
import { ProductDetailsDocument } from "@graphql/generated/sdk";
import type { Product as RawProduct, ProductDetailsQuery } from "@graphql/generated/types";
import { SizeLocales } from "@lib/constants/filtersConstants";
import { PageTypes } from "@lib/enums/PageTypes";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { useApi } from "@lib/helpers/useApi";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { normalizeProductData } from "@lib/product/helpers";
import type { Product } from "@lib/product/types";
import { graphQlClient } from "@lib/wrappers/graphqlWrapper";
import type { QueryClient } from "@tanstack/react-query";

interface ProductProps {
    sku: string;
    locale: string;
}

export const useProduct = ({ sku, locale }: ProductProps) =>
    useApi.useAsync([QueryKeys.getProduct, sku, locale], () => fetchProduct({ sku, locale }), {
        refetchOnMount: false,
        staleTime: Infinity,
    });

export const prefetchProduct = async (queryClient: QueryClient, { sku, locale }: ProductProps) =>
    useApi.usePrefetch(queryClient, [QueryKeys.getProduct, sku, locale], () =>
        fetchProduct({ sku, locale })
    );

export const fetchProduct = async ({ sku, locale }: ProductProps): Promise<Product | null> => {
    const startTime = performance.now();
    const { data, error } = await graphQlClient.queryCommerceTools<ProductDetailsQuery>(
        ProductDetailsDocument,
        {
            sku,
            storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
            channelId: process.env.NEXT_PUBLIC_CHANNEL_ID,
            distributionChannelWhereClause: `productSelection(id="${process.env.NEXT_PUBLIC_DISTRIBUTION_CHANNEL_ID}")`,
            currency: activeSiteCurrency,
            country: activeSiteCountry,
            commercetoolsLocale: locale,
            sizeConventions: getSizeConventionsForSite(),
        }
    );
    Logger.info(ServiceType.COMMERCE_TOOLS, "PDP CommerceTools Query", {
        tag: LogTag.PERFORMANCE,
        duration: performance.now() - startTime,
        pageType: PageTypes.PDP,
    });

    const product = data?.commercetoolsProduct?.product;
    if (!product?.productSelectionRefs?.exists || error) {
        Logger.warn(ServiceType.NEXT_API, "PDP Missing Product", {
            tag: LogTag.ERROR,
            message: "Couldn't find a product with the requested SKU",
            sku,
            source: ServiceType.COMMERCE_TOOLS,
        });

        return null;
    }

    return normalizeProductData(product as RawProduct);
};

function getSizeConventionsForSite() {
    return shoeConventionForSite.map((sizeConvention: SizeLocales) => {
        if (sizeConvention === SizeLocales.EU) {
            return "Size";
        } else {
            return `Size_${sizeConvention}`;
        }
    });
}
